/**
 * The arithmetic behind the review canvas — every bit of it pure, so it is unit
 * tested next door rather than eyeballed through a browser.
 *
 * Three coordinate spaces meet on that canvas and it is worth naming them once:
 *
 *   scan px    the uploaded image, origin top-left, y DOWN
 *   trace px   the same image downsampled for tracing (see `traceScaleFor`)
 *   mm         the canonical frame — origin bottom-left, y UP, 0.01 mm
 *
 * (The old review canvas's projection family lived here too; the Konva
 * editor stage owns viewing now — see `editor/camera.ts` — so only the
 * frame conversions and measurements remain.)
 *
 * `mmRingToScanPx` is the inverse of the px→mm mapping in two other places, and
 * the three have to stay one mapping: `app/lib/trace/units.ts` applies it when a
 * scan is traced, and `scanPxToGeomMm` in `src/worker/lib/saya/geometry.ts`
 * applies it again server-side when a sheet's calibration is corrected. Bounding
 * boxes likewise exist here and in the Worker's `geom2d.ts`; the app project
 * compiles nothing out of the Worker tree but `api/*.contract.ts`, which is why
 * the copies are structural rather than an oversight.
 */

import type { PartGeom, Ring } from '../../../worker/api/saya.contract'

/* ── Calibration ────────────────────────────────────────────────────────────── */

/**
 * What the factory's scanner is set to, in dots per inch.
 *
 * This is the fallback for a sheet that arrived with no calibration at all, and
 * it replaces an assumption that was not merely unmeasured but WRONG: the page
 * used to divide the image by a full A4 bed (210 × 297 mm). The scanner the
 * factory actually uses is an A3-class flatbed that CROPS TO CONTENT, so its
 * output is never the bed — a sheet with two small pieces on it came out
 * measured several times too big.
 *
 * 300 dpi is the preset on that machine and the density it stamps into every
 * file it writes, which is why the upload can usually read it (`file_dpi`) and
 * this constant is never reached. When it is reached the sheet is uncalibrated,
 * the screen says so, and approval refuses it server-side — the number exists
 * only so the operator can go on labelling shapes while the scale is unknown.
 */
export const FALLBACK_SCAN_DPI = 300

/** 300 dpi ≈ 11,81 px/mm — the figure the factory scanner stamps into its files. */
export const FALLBACK_PX_PER_MM = FALLBACK_SCAN_DPI / 25.4

export interface ScanScale {
  x: number
  y: number
  /** False when nothing measured this sheet — neither its file nor a human. */
  calibrated: boolean
}

/**
 * Pixels per millimetre for a scan.
 *
 * A calibrated scan carries its own figures, whether they were read out of the
 * file's own DPI stamp or set through the server's calibration endpoint (an
 * API-only recovery path — the screen no longer offers a form for it). The
 * screen distinguishes the two sources (`calibrationView`), the arithmetic
 * does not.
 *
 * An uncalibrated one still has to produce millimetres, because a draft is
 * allowed to be uncalibrated and the operator needs part outlines drawn on the
 * photograph while labelling. It gets the factory preset rather than a bed size,
 * and every measurement derived from it is marked as an assumption.
 */
export function effectivePxPerMm(scan: {
  pxPerMmX: number | null
  pxPerMmY: number | null
}): ScanScale {
  if (scan.pxPerMmX != null && scan.pxPerMmY != null) {
    return { x: scan.pxPerMmX, y: scan.pxPerMmY, calibrated: true }
  }
  return { x: FALLBACK_PX_PER_MM, y: FALLBACK_PX_PER_MM, calibrated: false }
}

/* ── Trace budget ───────────────────────────────────────────────────────────── */

/**
 * Longest edge the tracer is given. A 600 dpi A3 scan is ~7 000 px across and
 * tracing it in the page would take seconds for detail no cutter can use at
 * this stage; the contours are simplified to a quarter of a millimetre anyway.
 * The scale factor is folded back into px-per-mm, so millimetres come out the
 * same whatever the raster was.
 */
export const MAX_TRACE_EDGE_PX = 1600

/** ≤ 1, never upsamples. */
export function traceScaleFor(width: number, height: number): number {
  const longest = Math.max(width, height)
  if (!Number.isFinite(longest) || longest <= 0) return 1
  return Math.min(1, MAX_TRACE_EDGE_PX / longest)
}

/** Anything under a square centimetre is dust on the glass, not a part. */
export const MIN_PART_AREA_MM2 = 100

/** Contour simplification tolerance — a quarter of a millimetre. */
export const SIMPLIFY_TOLERANCE_MM = 0.25

/** Trace thresholds in the raster's own pixels, derived from its scale. */
export function traceOptionsFor(
  pxPerMmX: number,
  pxPerMmY: number,
): { minAreaPx: number; simplifyTolPx: number } {
  const perMm2 = pxPerMmX * pxPerMmY
  const simplifyTolPx = Math.max(
    0.5,
    SIMPLIFY_TOLERANCE_MM * ((pxPerMmX + pxPerMmY) / 2),
  )
  return {
    minAreaPx: Math.max(1, Math.round(MIN_PART_AREA_MM2 * perMm2)),
    simplifyTolPx,
  }
}

/* ── Bounds ─────────────────────────────────────────────────────────────────── */

export interface Bounds {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/** Null when there is nothing to bound (no rings, or only empty ones). */
export function ringsBounds(rings: readonly (readonly (readonly [number, number])[])[]): Bounds | null {
  let minX = Number.POSITIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY
  let seen = false

  for (const ring of rings) {
    for (const point of ring) {
      const [x, y] = point
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue
      seen = true
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
    }
  }

  return seen ? { minX, minY, maxX, maxY } : null
}

/** Bounds of a whole set of parts — outlines only; holes sit inside them. */
export function geomBounds(geoms: readonly PartGeom[]): Bounds | null {
  return ringsBounds(geoms.map((geom) => geom.outer))
}

/** A box wide/tall enough to be projected into — a single point is not. */
export function boundsSize(bounds: Bounds): { width: number; height: number } {
  return {
    width: Math.max(bounds.maxX - bounds.minX, 1e-6),
    height: Math.max(bounds.maxY - bounds.minY, 1e-6),
  }
}








/* ── Ring helpers ───────────────────────────────────────────────────────────── */

/**
 * Scales a ring in place-free fashion — used to lift a contour traced on the
 * downsampled raster back into the uploaded image's own pixels, so `srcPx` on
 * the wire always means "pixels of the file you uploaded".
 */
export function scaleRing(ring: readonly (readonly [number, number])[], factor: number): Ring {
  return ring.map(([x, y]): [number, number] => [x * factor, y * factor])
}

/**
 * Millimetres back into the uploaded scan's pixels — the exact inverse of the
 * trace lib's `pxToMm`.
 *
 * This is what lets a saved revision be drawn ON its scan again: the geometry
 * that comes back from the API is millimetres, the photo under it is pixels,
 * and the operator's whole check is that the two line up. Deriving it from the
 * scan's own px-per-mm (its own, or the fallback — the same numbers the trace
 * used) keeps that true without storing a second copy of the contours.
 *
 * It is also how a sheet whose pieces have already been saved rebuilds the
 * `srcPx` a wholesale parts write needs: `scanPxToGeomMm` in the Worker is
 * exactly this mapping in the other direction, so the round trip costs only the
 * 0,01 mm the stored millimetres were rounded to.
 */
export function mmRingToScanPx(
  ring: readonly (readonly [number, number])[],
  pxPerMmX: number,
  pxPerMmY: number,
  scanHeightPx: number,
): Ring {
  return ring.map(([x, y]): [number, number] => [x * pxPerMmX, scanHeightPx - y * pxPerMmY])
}

export function mmRingsToScanPx(
  rings: readonly (readonly (readonly [number, number])[])[],
  pxPerMmX: number,
  pxPerMmY: number,
  scanHeightPx: number,
): Ring[] {
  return rings.map((ring) => mmRingToScanPx(ring, pxPerMmX, pxPerMmY, scanHeightPx))
}

/** Centroid of a ring's points — the last resort for hanging a number on. */
export function ringCentroid(
  ring: readonly (readonly [number, number])[],
): [number, number] | null {
  if (ring.length === 0) return null
  let sumX = 0
  let sumY = 0
  for (const [x, y] of ring) {
    sumX += x
    sumY += y
  }
  return [sumX / ring.length, sumY / ring.length]
}

/* ── Interior point ─────────────────────────────────────────────────────────── */

/**
 * The x intervals a horizontal line at `y` spends INSIDE a set of rings, left
 * to right.
 *
 * Even-odd over every ring at once, which is what makes a hole cut the interval
 * it sits in rather than being counted as a second piece: a crossing of a hole
 * ring flips the parity exactly like a crossing of the outline does. Edges
 * exactly horizontal at `y` are skipped and the vertical test is half-open
 * (`y1 <= y < y2`), so a vertex the line passes through is counted once, not
 * twice.
 */
export function scanlineSpans(
  rings: readonly (readonly (readonly [number, number])[])[],
  y: number,
): [number, number][] {
  const crossings: number[] = []
  for (const ring of rings) {
    const count = ring.length
    for (let index = 0; index < count; index += 1) {
      const from = ring[index]
      const to = ring[(index + 1) % count]
      if (!from || !to) continue
      const [x1, y1] = from
      const [x2, y2] = to
      if (!Number.isFinite(y1) || !Number.isFinite(y2) || y1 === y2) continue
      const crosses = y1 <= y ? y2 > y : y2 <= y
      if (!crosses) continue
      crossings.push(x1 + ((y - y1) / (y2 - y1)) * (x2 - x1))
    }
  }
  crossings.sort((a, b) => a - b)

  const spans: [number, number][] = []
  for (let index = 0; index + 1 < crossings.length; index += 2) {
    spans.push([crossings[index]!, crossings[index + 1]!])
  }
  return spans
}

/**
 * Fractions of the bounding box height sampled when looking for a point inside
 * a piece. Mid-height first — that is the answer for almost every saya part —
 * then a spread either side for the ones where it is not.
 */
const INTERIOR_SAMPLE_FRACTIONS = [0.5, 0.45, 0.55, 0.4, 0.6, 0.32, 0.68, 0.22, 0.78] as const

/**
 * A point GUARANTEED to be inside the piece, for hanging its number on.
 *
 * The bounding-box centre is not that point, and on a real saya set it is
 * regularly the worst possible one: a U-shaped piece (a quarter, a collar) has
 * its bbox centre in the gap between the arms, where the operator sees the
 * number sitting on whatever smaller piece was nested there — which is exactly
 * how the factory misread the set on its first day.
 *
 * So the piece is sampled instead: the horizontal scanline at mid-height, the
 * WIDEST interval it spends inside the piece, and the middle of that interval.
 * The widest interval is what keeps the number off a thin arm where the digits
 * would overhang the outline anyway. Degenerate cases — a mid-height that only
 * grazes a corner, a shape whose waist is exactly at the middle — fall back to
 * more scanlines and, if all of them fail, to the centroid, which is at least
 * always defined.
 *
 * Works in whatever space the rings are given in (scan pixels, y down, or
 * canonical millimetres, y up): a horizontal line is horizontal in both.
 */
export function interiorPoint(
  outer: readonly (readonly [number, number])[],
  holes: readonly (readonly (readonly [number, number])[])[] = [],
): [number, number] | null {
  const bounds = ringsBounds([outer])
  if (!bounds) return null
  const rings = [outer, ...holes]
  const height = bounds.maxY - bounds.minY

  let best: { x: number; y: number; width: number } | null = null
  for (const fraction of INTERIOR_SAMPLE_FRACTIONS) {
    const y = bounds.minY + height * fraction
    for (const [from, to] of scanlineSpans(rings, y)) {
      const width = to - from
      if (width <= 0) continue
      if (best === null || width > best.width) best = { x: (from + to) / 2, y, width }
    }
    // The first scanline that lands inside the piece is the answer; the rest
    // exist for the shapes where it does not.
    if (best !== null) break
  }

  return best ? [best.x, best.y] : ringCentroid(outer)
}

/* ── Hit testing ────────────────────────────────────────────────────────────── */

/** True when the point is inside the outline and not inside one of its holes. */
export function ringsContain(
  point: readonly [number, number],
  outer: readonly (readonly [number, number])[],
  holes: readonly (readonly (readonly [number, number])[])[] = [],
): boolean {
  let inside = false
  for (const ring of [outer, ...holes]) {
    if (ringContains(point, ring)) inside = !inside
  }
  return inside
}

/** Crossing-number test against one ring, edges half-open as in `scanlineSpans`. */
function ringContains(
  point: readonly [number, number],
  ring: readonly (readonly [number, number])[],
): boolean {
  const [px, py] = point
  let inside = false
  const count = ring.length
  for (let index = 0; index < count; index += 1) {
    const from = ring[index]
    const to = ring[(index + 1) % count]
    if (!from || !to) continue
    const [x1, y1] = from
    const [x2, y2] = to
    if (y1 === y2) continue
    const crosses = y1 <= py ? y2 > py : y2 <= py
    if (!crosses) continue
    if (px < x1 + ((py - y1) / (y2 - y1)) * (x2 - x1)) inside = !inside
  }
  return inside
}
